import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import { authDelete, authGet, authPost, authPut } from "../../lib";

const useStyles = makeStyles((theme) => ({
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  title: { textTransform: "capitalize" },
  count: { color: theme.palette.text.secondary, marginLeft: theme.spacing(1) },
  search: { width: 260 },
  idCell: { width: 80, color: theme.palette.text.secondary },
  actions: { width: 96, textAlign: "right" },
  error: { color: theme.palette.error.main, marginBottom: theme.spacing(1) },
  // status_txt holds storefront HTML — show it as text, never render it.
  raw: {
    fontFamily: "monospace",
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    maxWidth: 380,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
  },
}));

const prettify = (s) => String(s).replace(/_/g, " ");

export function PreownedAttributesPage(props) {
  const classes = useStyles();
  const type = props?.pageRoute?.params?.type;

  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);

  const idKey = `${type}_id`;

  const load = useCallback(async () => {
    setError("");
    const { data, error: err } = await authGet(`/preowned/attributes/${type}`);
    if (err) return setError(err.message || "Failed to load");
    setRows(data?.rows ?? []);
  }, [type]);

  useEffect(() => {
    if (type) load();
    setFilter("");
  }, [type, load]);

  // Editable columns are whatever the table carries beyond its primary key,
  // so item_condition's `description` and item_status's extras appear on their
  // own without this screen knowing which tables have them.
  const columns = rows.length
    ? Object.keys(rows[0]).filter((k) => k !== idKey)
    : [type];

  const visible = filter
    ? rows.filter((r) =>
        columns.some((c) =>
          String(r[c] ?? "").toLowerCase().includes(filter.toLowerCase()),
        ),
      )
    : rows;

  async function save() {
    setError("");
    const body = {};
    for (const c of columns) body[c] = editing[c] ?? null;

    const { error: err } = editing[idKey]
      ? await authPut(`/preowned/attributes/${type}/${editing[idKey]}`, body)
      : await authPost(`/preowned/attributes/${type}`, body);

    if (err) return setError(err.message || "Save failed");
    setEditing(null);
    load();
  }

  async function remove(row) {
    if (!window.confirm(`Delete "${row[columns[0]]}"?`)) return;
    setError("");
    const { error: err } = await authDelete(
      `/preowned/attributes/${type}/${row[idKey]}`,
    );
    // The API refuses when items still reference the row — surface that.
    if (err) return setError(err.message || "Delete failed");
    load();
  }

  if (!type) return null;

  return (
    <Box p={3}>
      <div className={classes.header}>
        <Typography variant="h5" className={classes.title}>
          {prettify(type)}
          <Typography component="span" variant="body2" className={classes.count}>
            {rows.length} {rows.length === 1 ? "row" : "rows"}
          </Typography>
        </Typography>
        <Box display="flex" alignItems="center" gridGap={12}>
          <TextField
            className={classes.search}
            placeholder="Filter…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={() => setEditing({})}
          >
            Add
          </Button>
        </Box>
      </div>

      {error && <Typography className={classes.error}>{error}</Typography>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.idCell}>ID</TableCell>
              {columns.map((c) => (
                <TableCell key={c} style={{ textTransform: "capitalize" }}>
                  {prettify(c)}
                </TableCell>
              ))}
              <TableCell className={classes.actions} />
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((r) => (
              <TableRow key={r[idKey]} hover>
                <TableCell className={classes.idCell}>{r[idKey]}</TableCell>
                {columns.map((c) => (
                  <TableCell key={c}>
                    {/^</.test(String(r[c] ?? "")) ? (
                      <Tooltip title={String(r[c])}>
                        <span className={classes.raw}>{String(r[c])}</span>
                      </Tooltip>
                    ) : (
                      r[c]
                    )}
                  </TableCell>
                ))}
                <TableCell className={classes.actions}>
                  <IconButton size="small" onClick={() => setEditing({ ...r })}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => remove(r)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!visible.length && (
              <TableRow>
                <TableCell colSpan={columns.length + 2}>
                  <Typography variant="body2" color="textSecondary">
                    {rows.length ? "Nothing matches that filter." : "No rows."}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!editing} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle style={{ textTransform: "capitalize" }}>
          {editing?.[idKey] ? `Edit ${prettify(type)}` : `Add ${prettify(type)}`}
        </DialogTitle>
        <DialogContent>
          {columns.map((c) => (
            <TextField
              key={c}
              label={prettify(c)}
              value={editing?.[c] ?? ""}
              onChange={(e) => setEditing({ ...editing, [c]: e.target.value })}
              fullWidth
              margin="dense"
              multiline={c === "status_txt"}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button onClick={save} variant="contained" color="secondary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
export default PreownedAttributesPage;